import { memo, useEffect, useMemo } from "react";

import { useReactiveVar } from "@apollo/client";
import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import { Flag01 } from "@hl/base-components/lib/assets/icons/HDS";
import { BrandJustLogo } from "@hl/base-components/lib/assets/icons.generated";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import {
  PriceType,
  UnlockGateResult,
} from "@hl/shared-features/lib/apollo/graphql.generated";
import { envConfig } from "@hl/shared-features/lib/config";
import { GateConditionsModalProps } from "@hl/shared-features/lib/features/gate/GateConditionsModalBody";
import { GateConditionsResult } from "@hl/shared-features/lib/features/gate/GateConditionsResult";
import {
  AppSEO,
  NotFound,
  ServerError,
} from "@hl/shared-features/lib/features/layout";
import {
  Anchor,
  AppShell,
  Box,
  Center,
  Container,
  Flex,
  Group,
  MediaQuery,
  Space,
  Stack,
  Text,
  UnstyledButton,
  createStyles,
  useMantineTheme,
} from "@mantine/core";
import { useIntersection, useMediaQuery } from "@mantine/hooks";
import { useLocation } from "react-router-dom";

import { HOME_PAGE_URL } from "config";
import MintPageSkeleton from "~features/MintPage/MintPageSkeleton";
import { useMintPage } from "~hooks/useMintPage";
import { MintContext } from "~hooks/useMintState";

import { AppFooter } from "../layout/Footer";
import { AppHeader } from "../layout/Header";
import { GatedMintAllPrereqsModalProps } from "../layout/Modal/GatedMintAllPrereqsModal";
import {
  ModalType,
  modalVar,
  resetModalVar,
  setModalVar,
  updateModalVar,
} from "../layout/Modal/modal";

import { AuctionCard } from "./Auction/AuctionCard";
import { AuctionHistory } from "./Auction/AuctionHistory";
import Footer from "./Footer";
import { MintPageSEO } from "./MintPageSEO";
import { MintCard } from "./MintVector/MintCard";
import MintSaleAccordion from "./MintVector/MintSaleAccordion";
import { AuctionRulesCard } from "./components/AuctionRulesCard";
import BridgeLinks from "./components/BridgeLinks";
import { DetailSection } from "./components/DetailSection";
import { MintDetails } from "./components/DetailSection/MintDetails";
import { MintHero } from "./components/MintHero";
import { TokenDescription } from "./components/TokenDescription";
import { SHLOMS_404_FALLBACK_IMG_URL } from "./custom/Shloms404Carousel";
import Shloms404MintPage from "./custom/Shloms404MintPage";
import { useEmbedFlags } from "./embed/customize";
import MarketplacePreviewGallery from "./marketplace/MarketplacePreviewGallery";
import {
  GetSalePageAuctionsQuery,
  GetSalePageCollectionQuery,
  GetSalePageMintVectorsDocument,
  GetSalePageMintVectorsQuery,
} from "./queries.graphql.generated";
import { reportCollectionMailtoUri } from "./utils/uris";

export type BaseToken = {
  id?: string | null;
  name: string;
  description: string;
  image: string;
  onChainImage: string;
  size: number;
  animation_url?: string | null;
  nonTransferable?: boolean;
  ownerAddress?: string | null;
};

export const MintPageDetailSection = DetailSection;

export type Collection =
  | GetSalePageCollectionQuery["getPublicCollection"]
  | undefined;

export type MintVector =
  | GetSalePageMintVectorsQuery["getMintVectorsByCollection"][0]
  | undefined;

export type Auction =
  | GetSalePageAuctionsQuery["getAuctionsByCollection"][0]
  | undefined;

type MintPageProps = {
  collectionId?: string;
  embedCollectionId?: string;
  isEmbed?: boolean;
};

const useStyles = createStyles((theme) => {
  const mdUp = `@media (min-width: ${theme.breakpoints.md}px)`;

  return {
    title: {
      fontWeight: WEIGHT_BOLD,
    },

    layout: {
      flexDirection: "column",
      [mdUp]: {
        flexDirection: "row-reverse",
      },
    },
    leftColumn: {
      width: "100%",
      [mdUp]: {
        paddingRight: 64,
        flexGrow: 1,
      },
    },
    middleColumn: {
      width: "100%",
    },
  };
});

function useQuery(isEmbed: boolean) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { search } = !isEmbed ? useLocation() : window.location;

  return useMemo(() => new URLSearchParams(search), [search]);
}

const MintPage = ({
  embedCollectionId = "",
  isEmbed,
  collectionId,
}: MintPageProps) => {
  const { showModal } = useReactiveVar(
    modalVar as GatedMintAllPrereqsModalProps
  );
  const theme = useMantineTheme();
  const isTablet = useMediaQuery(`(max-width: ${theme.breakpoints.md - 1}px)`);
  const { ref: buttonRef, entry } = useIntersection({
    threshold: 0,
  });

  // Shows the mint card footer only if the main mint button was scrolled out of the viewport (scroll Y greater than mint button position).
  // If the user scrolls back up the footer won't show up (scroll Y lower than mint button).
  const showMintCardFooter = entry?.boundingClientRect
    ? !entry.isIntersecting && window.scrollY > entry.boundingClientRect.bottom
    : undefined;

  collectionId = collectionId ?? embedCollectionId;

  const mintPageHook = useMintPage(collectionId);
  const {
    collection,
    auction,
    mintVector,
    mintVectorsLoading,
    auctionsLoading,
    collectionLoading,
    creatorAddresses,
    handleMint,
    tokens,
    isSeriesMint,
    isCodeGenMint,
    isActiveMint,
    isAnySeriesMint,
    isCollectorChoiceMint,
    seriesTokenSelected,
    refetch,
    isNotFound,
    isMarketplaceNoticeShown,
    isMarketplaceEnabledForCollectionChain,
    isImported,
    setMintKey,
    isOptimizedLoading,
    mintSaleContentRef,
    appHeaderRef,
  } = mintPageHook;
  const { classes } = useStyles();
  const query = useQuery(isEmbed ?? false);

  const footerImageUrl = useMemo(() => {
    if (collection?.flagVariations.shloms404UI)
      return SHLOMS_404_FALLBACK_IMG_URL;
    if (isImported) {
      return collection?.importData?.logoUrl ?? "";
    }
    if (isSeriesMint) {
      return isCollectorChoiceMint
        ? seriesTokenSelected?.imageUrl ?? ""
        : collection?.seriesImages?.coverImageUrl;
    }
    if (isCodeGenMint) {
      return collection?.generativeDetails?.logoUrl ?? "";
    }
    return tokens[0].image ?? "";
  }, [
    isSeriesMint,
    isCollectorChoiceMint,
    seriesTokenSelected,
    collection,
    tokens,
    isCodeGenMint,
  ]);

  const userGateAccess = mintVector?.userGateAccess ?? auction?.userGateAccess;
  const description = isAnySeriesMint
    ? collection?.description
    : tokens[0].description;

  useEffect(() => {
    const gmk = query.get("gmk");
    if (gmk) {
      setMintKey(gmk);
    }
  }, [query]);

  // Needed to update modal data on refetch (userGateAccess)
  useEffect(() => {
    if (showModal === ModalType.GATED_ENTITY_ALL_PREREQS && userGateAccess) {
      setModalVar<GateConditionsModalProps>({
        showModal: ModalType.GATED_ENTITY_ALL_PREREQS,
        data: {
          creatorAddress: creatorAddresses ? creatorAddresses[0].address : "",
          creatorEns: collection?.creatorEns,
          refetchOnRequirementChange: [GetSalePageMintVectorsDocument],
          result: userGateAccess,
          handleDone: () => resetModalVar(),
          entity: "mint",
          isPreview: false,
        },
      });
    }
  }, [showModal, userGateAccess, creatorAddresses, collection]);
  const flags = useEmbedFlags();

  if (
    isOptimizedLoading &&
    (collectionLoading || auctionsLoading) &&
    (!collection || !mintVector)
  ) {
    return <MintPageSkeleton />;
  }

  if (
    (isOptimizedLoading == null ||
      collectionLoading ||
      (mintVectorsLoading && !isOptimizedLoading) ||
      auctionsLoading) &&
    (!collection || !mintVector)
  ) {
    return <LoadingPage />;
  }
  if (isNotFound) {
    return (
      <NotFound
        redirectUrl={HOME_PAGE_URL.base}
        buttonLabel="Return to Homepage"
      />
    );
  }

  const noSaleForOptimizedLoading =
    !mintVector && !mintVectorsLoading && !auction && !isImported;
  const noSaleForUnoptimizedLoading = !mintVector && !auction && !isImported;

  if (
    !collection ||
    (!isAnySeriesMint &&
      ((isOptimizedLoading && noSaleForOptimizedLoading) ||
        (!isOptimizedLoading && noSaleForUnoptimizedLoading)))
  ) {
    return (
      <ServerError
        redirectUrl={HOME_PAGE_URL.base}
        buttonLabel="Return to Homepage"
      />
    );
  }

  const showAllGatePrereqs = (
    result: UnlockGateResult,
    creatorAddress?: string | null,
    creatorEns?: string | null
  ) => {
    return () => {
      updateModalVar<GateConditionsModalProps>({
        showModal: ModalType.GATED_ENTITY_ALL_PREREQS,
        data: {
          creatorAddress,
          creatorEns,
          refetchOnRequirementChange: [GetSalePageMintVectorsDocument],
          result,
          handleDone: () => resetModalVar(),
          entity: "mint",
          isPreview: false,
        },
      });
    };
  };

  const onMintClick = isCollectorChoiceMint
    ? (chainId: number) =>
        handleMint(
          chainId,
          seriesTokenSelected?.id,
          seriesTokenSelected?.imageUrl || ""
        )
    : handleMint;

  const showGatePrereqs = userGateAccess
    ? showAllGatePrereqs(
        userGateAccess,
        creatorAddresses ? creatorAddresses[0].address : undefined,
        collection.creatorEns
      )
    : undefined;

  return (
    <MintContext.Provider value={mintPageHook}>
      <AppShell
        header={
          <>
            {!isEmbed && (
              <>
                <AppSEO
                  title={`${collection.name} - Token Collection`}
                  description={description}
                />
                <MintPageSEO collection={collection} />
              </>
            )}
            <Box ref={appHeaderRef}>
              <AppHeader />
            </Box>
          </>
        }
        footer={
          !mintVectorsLoading ? (
            <AppFooter showFooter={showMintCardFooter}>
              <Footer
                imageUrl={footerImageUrl}
                hasBid={true}
                onMintClick={onMintClick}
                seriesTokeMinted={
                  isCollectorChoiceMint &&
                  (!seriesTokenSelected?.imageUrl ||
                    seriesTokenSelected?.minted ||
                    false)
                }
              />
            </AppFooter>
          ) : undefined
        }
      >
        {collection.flagVariations.shloms404UI ? (
          <Shloms404MintPage
            collectionId={collectionId}
            mintVectorsLoading={mintVectorsLoading}
            auctionsLoading={auctionsLoading}
            userGateAccess={userGateAccess ?? undefined}
            mintVector={mintVector}
            isTablet={isTablet}
            collection={collection}
            creatorAddresses={creatorAddresses ?? undefined}
            description={description ?? undefined}
            classes={classes}
            isMarketplaceEnabledForCollectionChain={
              isMarketplaceEnabledForCollectionChain
            }
            isMarketplaceNoticeShown={isMarketplaceNoticeShown}
            theme={theme}
            buttonRef={buttonRef}
            onMintClick={onMintClick}
            refetch={refetch}
            showGatePrereqs={showGatePrereqs}
          />
        ) : (
          <>
            <MintHero />
            {!isAnySeriesMint && <Space h={40} />}
            <Container
              size="md"
              mb={mintVector?.mintVectorStats?.sold ?? 0 > 0 ? 120 : 140}
            >
              <Flex className={classes.layout}>
                <Box ref={mintSaleContentRef}>
                  <>
                    {!mintVectorsLoading &&
                      !auctionsLoading &&
                      userGateAccess &&
                      showGatePrereqs && (
                        <>
                          <Space h="xs" />
                          <GateConditionsResult
                            creatorAddress={
                              creatorAddresses
                                ? creatorAddresses[0].address
                                : undefined
                            }
                            creatorEns={collection.creatorEns}
                            refetchOnRequirementChange={[
                              GetSalePageMintVectorsDocument,
                            ]}
                            handleShowModal={showGatePrereqs}
                            unlockGateResult={userGateAccess}
                            showFirstN={4}
                            showConnectWallet={false}
                            entity={auction ? "auction" : "mint"}
                            multipleMintVectorEnableMintPage={
                              collection.flagVariations
                                .multipleMintVectorsEnableMintPage
                            }
                          />
                          <Space h="xl" />
                        </>
                      )}
                    {((!isOptimizedLoading && mintVector) ||
                      (isOptimizedLoading &&
                        (mintVectorsLoading || mintVector))) && (
                      <Stack w={isTablet ? "100%" : 380}>
                        {collection.flagVariations
                          .multipleMintVectorsEnableMintPage &&
                          mintVector && (
                            <MintSaleAccordion
                              handleShowModal={showGatePrereqs}
                              unlockGateResult={userGateAccess}
                            />
                          )}
                        {(!isMarketplaceNoticeShown ||
                          (mintVector &&
                            mintVector.priceType ===
                              PriceType.DutchAuction)) && (
                          <MintCard
                            ref={buttonRef}
                            onMintClick={onMintClick}
                            onTimesUp={refetch}
                          />
                        )}
                      </Stack>
                    )}
                    {isActiveMint && (
                      <BridgeLinks
                        mt={40}
                        chainId={collection.chainId}
                        paymentCurrency={mintVector?.paymentCurrency}
                      />
                    )}
                    {(mintVector || isImported) &&
                      collection &&
                      isAnySeriesMint &&
                      isMarketplaceEnabledForCollectionChain && (
                        <Stack
                          w={isTablet ? "100%" : 380}
                          mt={isImported ? -40 : 0}
                        >
                          <MarketplacePreviewGallery
                            collectionId={collectionId}
                          />
                        </Stack>
                      )}
                    {auction && (
                      <>
                        <AuctionCard />
                        <Space h="xl" />
                        <AuctionHistory />
                        <Space h="xl" />
                        <AuctionRulesCard
                          customBufferMinutes={
                            auction.customBufferMinutes ?? undefined
                          }
                        />
                        <Space h={45} />
                      </>
                    )}
                    {!isActiveMint && (
                      <BridgeLinks
                        mt={auction ? 0 : 40}
                        chainId={collection.chainId}
                        paymentCurrency={mintVector?.paymentCurrency}
                      />
                    )}
                  </>
                </Box>
                <Box className={classes.leftColumn}>
                  <MediaQuery largerThan="md" styles={{ display: "none" }}>
                    <Space h={40} />
                  </MediaQuery>
                  {description && !flags.strabismusTemplate && (
                    <TokenDescription description={description} />
                  )}
                  <MintDetails collectionId={collectionId} mt={40} />
                </Box>
              </Flex>
            </Container>
            {!flags.strabismusTemplate && (
              <Center pb={{ base: 60, xl: 60 }} p={20}>
                <UnstyledButton
                  component="a"
                  href={reportCollectionMailtoUri(collection)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Group spacing={6}>
                    <Flag01 width={14} />
                    <Text size="sm">Report this collection</Text>
                  </Group>
                </UnstyledButton>
              </Center>
            )}
            <Flex justify="center">
              <Anchor
                href={envConfig.home.baseUrl}
                target="_blank"
                rel="noreferrer"
              >
                <BrandJustLogo
                  height={14}
                  width={20}
                  color={theme.colors.primaryText[0]}
                />
              </Anchor>
            </Flex>
            <Space h={20} />
          </>
        )}
      </AppShell>
    </MintContext.Provider>
  );
};

export default memo(MintPage);
